import { CalendarOutlined, PlusOutlined } from "@ant-design/icons"
import { Button } from "components/Button/Button"
import { Checkbox } from "components/Checkbox/Checkbox"
import { Divider } from "components/Divider/Divider"
import { ErrorStatus } from "components/ErrorStatus/ErrorStatus"
import { Image } from "components/Image/Image"
import { Modal } from "components/Modal/Modal"
import { Tag } from "components/Tag/Tag"
import { useDisclosure } from "hooks/useDisclosure"
import { useInfiniteScroll } from "hooks/useInfiniteScroll"
import { WeightIcon } from "icons/WeightIcon"
import { Escalation } from "interface/escalation"
import { Patient, TimelineItem, TimelineScreening } from "interface/patientType"
import { useNavigateToScreening } from "modules/dashboard/useNavigateToScreening"
import { WeightLabel } from "modules/dashboard/WeightLabel"
import { ComponentProps, HTMLAttributes, useMemo, useState } from "react"
import { useUpdateEscalationScreenings } from "services/escalations"
import { usePatientScreenings, usePatientTimeline } from "services/patients"
import { cn } from "styles/cn"
import { humanReadable } from "utils"
import { GENERIC_ERROR_MESSAGE } from "utils/constants"
import { dateFormatter } from "utils/date"

export function EscalationLinkedScreenings({
	className,
	escalation,
	patient,
	...props
}: ComponentProps<"div"> & {
	escalation: Escalation
	patient: Patient
}) {
	const { patientId, linkedScreeningIds, status } = escalation
	const addModal = useDisclosure()
	const navigate = useNavigateToScreening()
	const isOpen = status === "OPEN"
	const { data: screenings = [] } = usePatientScreenings(
		patientId,
		linkedScreeningIds,
	)

	return (
		<div {...props} className={cn("flex flex-col gap-3", className)}>
			<div className="flex flex-wrap justify-between gap-3">
				<h2 className="text-xl">Related screenings</h2>
				{isOpen && (
					<Button icon={<PlusOutlined aria-hidden />} onClick={addModal.open}>
						Add screenings
					</Button>
				)}
			</div>

			{screenings.length === 0 && (
				<p className="text-navy-60">No screenings added.</p>
			)}

			{screenings.length > 0 && (
				<ul
					className="flex flex-col gap-3"
					aria-label={`${screenings.length} screening(s)`}
				>
					{screenings.map((screening) => {
						const { id, capturedAt, screeningNote } = screening
						const labels = screeningNote?.labels ?? []
						const timestamp = dateFormatter(capturedAt, {
							hasYear: false,
						})

						return (
							<li key={id}>
								<ItemScreening
									as="button"
									type="button"
									patient={patient}
									screening={screening}
									aria-label={`Screening at ${timestamp}`}
									className="focus-outline cursor-pointer text-left hover:border-green-60"
									onClick={() => navigate(id)}
								>
									{labels.length > 0 && (
										<>
											<Divider
												variant="dashed"
												className="my-3 border-grey-20"
											/>

											<div className="flex flex-wrap gap-2">
												{screeningNote?.labels.map((label) => (
													<Tag variant="filled" key={label} className="m-0">
														{humanReadable(label)}
													</Tag>
												))}
											</div>
										</>
									)}
								</ItemScreening>
							</li>
						)
					})}
				</ul>
			)}

			{addModal.isOpen && (
				<AddScreenings
					patient={patient}
					escalation={escalation}
					onClose={addModal.close}
				/>
			)}
		</div>
	)
}

const ItemScreening = ({
	as: Component = "li",
	screening,
	patient,
	className,
	children,
	...props
}: HTMLAttributes<HTMLElement> & {
	as?: "li" | "div" | "button"
	screening: TimelineScreening
	patient: Patient
	type?: "button" | "submit" | "reset"
}) => {
	const { imageThumbS3Key, capturedAt, isReviewed } = screening
	const timestamp = dateFormatter(capturedAt, {
		hasYear: false,
	})
	const weekday = dateFormatter(capturedAt, {
		onlyWeekday: true,
		needSimplify: true,
	})

	return (
		<Component
			{...props}
			className={cn(
				"@container flex w-full flex-col rounded-[20px] border border-mint-20 bg-white p-5 shadow-secondary",
				className,
			)}
		>
			<div className="grid grid-cols-[120px_1fr] place-items-start gap-2 @2xl:grid-cols-[max-content_1fr_1fr] @2xl:place-items-center">
				<div className="row-span-2 place-content-center @2xl:row-span-1">
					{!imageThumbS3Key && (
						<div className="flex items-center justify-center">
							<WeightIcon.Null className="size-20 text-navy-60" />
						</div>
					)}

					{imageThumbS3Key && (
						<Image
							src={imageThumbS3Key}
							alt={`Screening at ${timestamp}`}
							className="size-20 rounded-2xl bg-grey-30 object-cover"
						/>
					)}
				</div>

				<div className="flex items-start gap-5 @2xl:items-center @2xl:justify-self-end">
					<div
						className={cn(
							"grid min-h-11 min-w-11 place-items-center rounded-xl bg-grey-10",
							!isReviewed && "bg-purple-0",
						)}
					>
						<CalendarOutlined
							aria-hidden
							className={cn(
								"text-2xl text-navy-60",
								!isReviewed && "text-purple-80",
							)}
						/>
					</div>
					<div className="flex flex-col items-start">
						<p className="flex flex-wrap items-center gap-2">
							<span className="text-lg font-bold">{weekday}</span>
							{!isReviewed && (
								<Tag variant="filled" color="blue">
									New
								</Tag>
							)}
						</p>
						<p className="text-lg text-navy-60">{timestamp}</p>
					</div>
				</div>

				<div className="-col-end-1 flex items-start justify-start gap-5 @2xl:items-center">
					<div className="grid min-h-11 min-w-11 place-items-center rounded-xl bg-grey-10">
						<WeightIcon className="size-6 fill-navy-60" aria-hidden />
					</div>

					<WeightLabel
						fullName={patient.fullName}
						dataPoint={screening}
						className="font-bold"
					/>
				</div>
			</div>

			{children}
		</Component>
	)
}

function AddScreenings({
	patient,
	escalation,
	onClose,
}: {
	patient: Patient
	escalation: Escalation
	onClose: VoidFunction
}) {
	const [targetScroll, setTargetScroll] = useState<HTMLDivElement | null>(null)
	const {
		data: { pages: items = [] } = {},
		hasNextPage,
		isFetching,
		isFetchingNextPage,
		fetchNextPage,
	} = usePatientTimeline({
		patientId: escalation.patientId,
		types: SCREENING_TYPES,
	})
	const [selectedIds, setSelectedIds] = useState<string[]>(
		escalation.linkedScreeningIds ?? [],
	)
	const updateScreenings = useUpdateEscalationScreenings(escalation.id)
	const screenings = useMemo(
		() => items.map((item) => item.data as TimelineScreening),
		[items],
	)
	const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])
	const isLoading = isFetching || isFetchingNextPage
	const toggleSelection = (id: string) => {
		setSelectedIds((prev) =>
			prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
		)
	}
	const handleOk = async () => {
		updateScreenings.mutate(
			{
				linkedScreeningIds: selectedIds,
			},
			{ onSuccess: onClose },
		)
	}

	useInfiniteScroll({
		target: targetScroll,
		hasMore: hasNextPage,
		isLoading,
		onLoadMore: fetchNextPage,
	})

	return (
		<Modal
			open
			fullscreen
			width={1080}
			cancelText="Cancel"
			title="Add screenings"
			okText="Update selection"
			mask={{ closable: false, enabled: false }}
			confirmLoading={updateScreenings.isPending}
			classNames={{
				body: "flex flex-col gap-4 overflow-hidden",
			}}
			onOk={handleOk}
			onCancel={onClose}
		>
			<p>You may add as many screenings as you like.</p>
			<div
				ref={setTargetScroll}
				className="flex flex-col gap-3 overflow-y-auto p-2"
			>
				{screenings.map((screening) => {
					const isChecked = selectedSet.has(screening.id)
					const timestamp = dateFormatter(screening.capturedAt, {
						hasYear: false,
					})

					return (
						<div
							key={screening.id}
							role="checkbox"
							aria-checked={isChecked}
							tabIndex={0}
							className="focus-outline flex cursor-pointer gap-3 rounded-3xl outline-offset-3!"
							aria-label={`Screening at ${timestamp}`}
							onClick={() => toggleSelection(screening.id)}
							onKeyDown={(e) => {
								if (e.key === "Enter" || e.key === " ") {
									e.preventDefault()
									toggleSelection(screening.id)
								}
							}}
						>
							<Checkbox
								checked={isChecked}
								tabIndex={-1}
								aria-hidden="true"
								className="pointer-events-none"
							/>

							<ItemScreening as="div" patient={patient} screening={screening} />
						</div>
					)
				})}
			</div>
			<ErrorStatus show={updateScreenings.isError}>
				{updateScreenings.error?.message ?? GENERIC_ERROR_MESSAGE}
			</ErrorStatus>
		</Modal>
	)
}

const SCREENING_TYPES: TimelineItem["itemType"][] = ["screening"]
